import { Grid, GridCell, GridRow } from "@natera/material/lib/layout";
import { Button, Form, FormField, Textfield } from "@natera/form";
import React from "react";
import { defineMessages, useIntl } from "react-intl";
import { useErrorController } from "@natera/platform/lib/hooks";

const messages = defineMessages({
  provideGovernmentIssuedFormEnterNumber: {
    id: "provideGovernmentIssuedFormEnterNumber",
    defaultMessage: "Enter government-issued insurance number",
  },
  provideGovernmentIssuedFormReEnterNumber: {
    id: "provideGovernmentIssuedFormReEnterNumber",
    defaultMessage: "Re-enter government-issued insurance number",
  },
  provideGovernmentIssuedFormPlaceholder: {
    id: "provideGovernmentIssuedFormPlaceholder",
    defaultMessage: "Enter insurance number",
  },
  provideGovernmentIssuedFormRequired: {
    id: "provideGovernmentIssuedFormRequired",
    defaultMessage: "This information is required.",
  },
  provideGovernmentIssuedFormMatch: {
    id: "provideGovernmentIssuedFormMatch",
    defaultMessage: "Insurance numbers must match",
  },
  provideGovernmentIssuedFormInvalid: {
    id: "provideGovernmentIssuedFormInvalid",
    defaultMessage: "Please enter a valid insurance number",
  },
  provideGovernmentIssuedFormButtonNext: {
    id: "provideGovernmentIssuedFormButtonNext",
    defaultMessage: "Next",
  },
  provideGovernmentIssuedFormButtonBack: {
    id: "provideGovernmentIssuedFormButtonBack",
    defaultMessage: "Back",
  },
});

type Props = {
  defaultValue?: string;
  onSubmit: (insuranceNumber: string) => void;
  goBack: () => void;
};

const ProvideGovernmentIssuedForm: React.FC<Props> = ({
  defaultValue,
  onSubmit,
  goBack,
}) => {
  const intl = useIntl();
  const errorController = useErrorController();

  React.useEffect(() => {
    const alerts = document.querySelectorAll(
      ".mdc-text-field-helper-text[role='alert']"
    );
    alerts.forEach((el) => el.removeAttribute("role"));
  });

  const handleSubmit: React.FormEventHandler<HTMLFormElement> = (event) => {
    event.preventDefault();
    errorController.clearErrors();

    const formData = new FormData(event.currentTarget);
    const insuranceNumber = formData.get("insuranceNumber")?.toString() || "";
    const reInsuranceNumber =
      formData.get("reInsuranceNumber")?.toString() || "";

    const errorMessages = {
      required: intl.formatMessage(
        messages.provideGovernmentIssuedFormRequired
      ),
      invalid: intl.formatMessage(messages.provideGovernmentIssuedFormInvalid),
      mismatch: intl.formatMessage(messages.provideGovernmentIssuedFormMatch),
    };

    const trimmedFirst = insuranceNumber.trim();
    const trimmedSecond = reInsuranceNumber.trim();

    const validateField = (
      value: string,
      trimmedValue: string
    ): string | null => {
      if (!value) return errorMessages.required;
      if (value !== trimmedValue) return errorMessages.invalid;
      return null;
    };

    const errors: Record<string, string> = {};

    const firstError = validateField(insuranceNumber, trimmedFirst);
    const secondError = validateField(reInsuranceNumber, trimmedSecond);

    if (firstError) errors.insuranceNumber = firstError;
    if (secondError) errors.reInsuranceNumber = secondError;

    if (!firstError && !secondError && trimmedFirst !== trimmedSecond) {
      errors.insuranceNumber = errorMessages.mismatch;
      errors.reInsuranceNumber = errorMessages.mismatch;
    }

    if (Object.keys(errors).length > 0) {
      Object.entries(errors).forEach(([field, message]) => {
        errorController.setValidationError(field, message);
      });

      const firstErrorField = document.getElementById(Object.keys(errors)[0]);
      firstErrorField?.focus();
      return;
    }

    onSubmit(trimmedFirst);
  };

  return (
    <Form
      noValidate
      onSubmit={handleSubmit}
      buttons={
        <>
          <Button type="submit" unelevated>
            {intl.formatMessage(messages.provideGovernmentIssuedFormButtonNext)}
          </Button>
          <Button onClick={goBack}>
            {intl.formatMessage(messages.provideGovernmentIssuedFormButtonBack)}
          </Button>
        </>
      }
    >
      <Grid>
        <GridRow>
          <GridCell span={12}>
            <FormField
              title={intl.formatMessage(
                messages.provideGovernmentIssuedFormEnterNumber
              )}
              label={intl.formatMessage(
                messages.provideGovernmentIssuedFormEnterNumber
              )}
              htmlFor="insuranceNumber"
              required
              error={errorController.getValidationError("insuranceNumber")}
            >
              <Textfield
                id="insuranceNumber"
                name="insuranceNumber"
                type="text"
                placeholder={intl.formatMessage(
                  messages.provideGovernmentIssuedFormPlaceholder
                )}
                defaultValue={defaultValue}
                outline
                required
                aria-describedby={
                  errorController.getValidationError("insuranceNumber")
                    ? "insuranceNumber-error"
                    : undefined
                }
              />
            </FormField>
          </GridCell>

          <GridCell span={12}>
            <FormField
              title={intl.formatMessage(
                messages.provideGovernmentIssuedFormReEnterNumber
              )}
              label={intl.formatMessage(
                messages.provideGovernmentIssuedFormReEnterNumber
              )}
              htmlFor="reInsuranceNumber"
              required
              error={errorController.getValidationError("reInsuranceNumber")}
            >
              <Textfield
                id="reInsuranceNumber"
                name="reInsuranceNumber"
                placeholder={intl.formatMessage(
                  messages.provideGovernmentIssuedFormPlaceholder
                )}
                defaultValue={defaultValue}
                outline
                required
                aria-describedby={
                  errorController.getValidationError("reInsuranceNumber")
                    ? "reInsuranceNumber-error"
                    : undefined
                }
              />
            </FormField>
          </GridCell>
        </GridRow>
      </Grid>
    </Form>
  );
};

export default ProvideGovernmentIssuedForm;
